import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal from '@uikit/Modal/Modal';
import {
  AccountSettingsModalProps,
  defaultValues,
  formDataType,
} from '@src/modals/AccountSettingsModal/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectUsers, setUserInfo } from '@store/slices/users/usersSlice';
import ImageUpload from '@uikit/FormItems/ImageUpload/ImageUpload';
import { IMAGES } from '@image/images';
import { IMAGE_STORAGE_URL, X_HEADER_ID_NAME } from '@utils/constants';
import { PointIcon } from '@src/svg';
import { ControlledInput } from '@uikit/FormItems/ControlledItems';
import { useForm } from '@hooks/useDirtyForm';
import { useAuthLoginQuery, useChangePasswordMutation } from '@store/rtk/auth';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { sessionSetter, updateXHeaderId } from '@utils/storage';
import { toast } from 'react-toastify';
import { validatePassword } from '@utils/PasswordRequirments';

import { useUploadImageMutation } from '@store/rtk/global';
import { uploadImageByPath } from '@utils/uploadImageByPath';
import { usePasswordMatch } from '@hooks/usePasswordMatch';
import { StatusWrapper } from '@component/StatusWrapper/StatusWrapper';
import {
  AppleSignInButton,
  GoogleSignInButton,
  MicrosoftSignInButton,
} from '@component/SSO/buttons';
import { extractBooleanValue, toastError } from '@utils/helpers';
import { MICROSOFT_TOGGLE_STORAGE_KEY } from '@uikit/Tables/TableDynamic/TableDynamic';
import TruncatedText from '@component/TruncatedText/TruncatedText';
import { toastMessage } from '@utils/toastMessage';

const isMsButtonVisible = extractBooleanValue(
  window.localStorage.getItem(MICROSOFT_TOGGLE_STORAGE_KEY)
);
const AccountSettingsModal: FC<AccountSettingsModalProps> = ({
  closeModal,
}) => {
  const dispatch = useAppDispatch();
  const [changePassword, { isLoading: passwordLoading }] =
    useChangePasswordMutation();

  const [uploadImage, { isLoading: imageLoading }] = useUploadImageMutation();

  const xHeaderId: any = sessionSetter.getItem(X_HEADER_ID_NAME);

  const { userInfo } = useAppSelector(selectUsers);
  const { data: profileUserInfo } = useAuthLoginQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const ssoProviderTypes =
    profileUserInfo?.ssoProviderType?.map((provider: string) =>
      provider.toUpperCase()
    ) || [];
  const isAppleSsoActivated = ssoProviderTypes.includes('APPLE');
  const isGoogleSsoActivated = ssoProviderTypes.includes('GOOGLE');
  const isMicrosoftSsoActivated = ssoProviderTypes.includes('MICROSOFT');

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { dirtyFields, isSubmitting, isValid },
  } = useForm<formDataType>({
    defaultValues,
  });

  const [saving, setSaving] = useState(false);

  const { pass, confirmPass, isPasswordsMismatch, isPasswordEmpty } =
    usePasswordMatch({
      watch,
      setError,
      clearErrors,
      passwordField: 'password',
      confirmField: 'confirmPassword',
    });

  const isPasswordValid = validatePassword(pass);
  const hasPasswordInput = !!pass || !!confirmPass;

  useEffect(() => {
    updateXHeaderId('user', userInfo?.id);
  }, []);

  const doSubmit = useCallback(
    async (body: formDataType) => {
      const { confirmPassword, password, image } = body;

      if (password && !validatePassword(password)) {
        toast.error(toastMessage.COMMON.passwordDoesNotMeetAllRequirements);
        return;
      }

      if (password && confirmPassword && password !== confirmPassword) {
        toast.error(toastMessage.COMMON.confirmPasswordMismatch);
        return;
      }

      setSaving(true);
      try {
        if (image instanceof File) {
          const uploadPath = `payor/users/${xHeaderId?.user || userInfo?.id}`;
          const uploadedPath = await uploadImageByPath(
            uploadImage,
            image,
            uploadPath
          );

          if (userInfo) {
            const updatedUserInfo = { ...userInfo, image: uploadedPath };
            dispatch(setUserInfo(updatedUserInfo));
            sessionSetter.setItem('userInfo', updatedUserInfo);
          }

          setValue('image' as any, null, { shouldDirty: false });

          toast.success(toastMessage.ACCOUNT.profileImageUpdated);
        }

        if (password) {
          const res = await changePassword({ body: password }).unwrap();
          if (res)
            toast.success(toastMessage.COMMON.passwordChangedSuccessfully);
        }

        closeModal();
      } catch (e: any) {
        toastError(e, toastMessage.ACCOUNT.failedToSaveAccountSettings);
      } finally {
        setSaving(false);
      }
    },
    [
      changePassword,
      closeModal,
      setValue,
      dispatch,
      uploadImage,
      xHeaderId?.user,
      userInfo?.id,
      userInfo,
    ]
  );

  return (
    <Modal
      title="Account Settings"
      closeClick={closeModal}
      maxWidth="372px"
      className="note_modal"
      dataTestId="account-settings"
    >
      <div className="account-settings_modal__body" data-test-id="acct-modal">
        <div className="member-avatar-header" data-test-id="acct-avatar-sec">
          <div className="member-avatar-header-left">
            <StatusWrapper size={10} statusColor={userInfo?.status}>
              <ImageUpload
                currentImage={
                  userInfo?.image
                    ? `${IMAGE_STORAGE_URL}/${userInfo.image}`
                    : IMAGES.defaultImage
                }
                selectedImage={watch('image') ?? null}
                fallBackImage={IMAGES.defaultImage}
                path={`payor/users/${xHeaderId?.user}`}
                setValue={setValue}
              />
            </StatusWrapper>
            {userInfo && (
              <div
                className="member-avatar-header-info"
                data-test-id="acct-user-info"
              >
                <div className="member-avatar-header-name">
                  <TruncatedText
                    text={`${userInfo?.firstName} ${userInfo?.lastName}`.trim()}
                    dataTestId="acct-user-name"
                  />
                  <span
                    className={`member-status ${
                      userInfo?.active ? 'active' : 'inactive'
                    }`}
                  >
                    {userInfo?.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="member-avatar-payer">
                  {userInfo?.roles?.label || ''}
                  <PointIcon />
                  {userInfo?.email || ''}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="account-settings-form">
          <h1 data-test-id="acct-pwd-title">Change Password</h1>

          <ControlledInput
            control={control}
            name="password"
            title="New password"
            type="password"
            showPasswordStrength
            confirmPass={confirmPass}
            dataTestId="acct-pwd-new"
            rules={{
              validate: (value) =>
                !value ||
                validatePassword(value) ||
                'Password does not meet all requirements',
            }}
          />

          <ControlledInput
            control={control}
            name="confirmPassword"
            title="Confirm Password"
            type="password"
            dataTestId="acct-pwd-confirm"
            rules={{
              validate: (value) =>
                value === getValues('password') ||
                'Confirm Password and New Password do not match',
            }}
          />

          {/*TODO this might be used for the future*/}
          <div className="account-settings-sso">
            <div className="account-or-block">
              <span className="line"></span>
              <span className="line-text-or">Or</span>
              <span className="line"></span>
            </div>
            <div className="account-sso-buttons">
              <div className="social_buttons">
                <AppleSignInButton
                  activated={isAppleSsoActivated}
                  type="attach"
                />
                <GoogleSignInButton
                  activated={isGoogleSsoActivated}
                  type="attach"
                />
                {isMsButtonVisible && (
                  <MicrosoftSignInButton
                    activated={isMicrosoftSsoActivated}
                    type="attach"
                  />
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            data-test-id="acct-save"
          >
            <SubmitButton
              title="Save"
              style={{ maxWidth: 56 }}
              dataTestId="acct-save"
              onClick={handleSubmit(doSubmit)}
              dirtyFields={dirtyFields}
              loading={
                isSubmitting || passwordLoading || imageLoading || saving
              }
              disabled={
                isSubmitting ||
                passwordLoading ||
                imageLoading ||
                saving ||
                (hasPasswordInput &&
                  (isPasswordsMismatch || isPasswordEmpty || !isPasswordValid))
              }
              forceDisable={!isValid}
              disableWhenUnchangedFields
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AccountSettingsModal;
